import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../api/client";

// Compact date-heatmap: rows = resources, columns = dates. Cells shaded by
// allocation %. Red border when the day exceeds the resource's max_units.
export default function UsageTab({ projectId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!projectId) { setRows([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/plan/resources/usage", { params: { project_id: projectId } });
      setRows(data.usage || []);
    } catch { setRows([]); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  // Build a sorted list of every date any resource has activity on.
  const allDates = Array.from(new Set(rows.flatMap((r) => r.days.map((d) => d.date)))).sort();

  return (
    <div className="flex-1 overflow-auto p-6" data-testid="usage-tab">
      {loading ? (
        <div className="text-sm text-neutral-500">Loading usage…</div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-neutral-200 rounded-xl p-10 text-center text-sm text-neutral-500">
          Add resources and assign them to plan tasks to see allocation and over-allocation warnings here.
        </div>
      ) : allDates.length === 0 ? (
        <div className="bg-white border border-neutral-200 rounded-xl p-10 text-center text-sm text-neutral-500">
          No task assignments yet. Open a plan task and assign resources.
        </div>
      ) : (
        <div className="bg-white border border-neutral-200 rounded-xl overflow-auto" data-testid="usage-heatmap">
          <table className="text-xs border-collapse">
            <thead>
              <tr className="border-b border-neutral-200">
                <th className="sticky left-0 bg-white z-10 text-left px-3 py-2 min-w-[220px] border-r border-neutral-200">Resource</th>
                {allDates.map((d) => (
                  <th key={d} className="px-1 py-1 text-[9px] text-neutral-400 font-mono whitespace-nowrap" style={{ minWidth: 24 }}>
                    {d.slice(5)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => {
                const dayMap = Object.fromEntries(u.days.map((d) => [d.date, d]));
                const overFlag = u.over_days > 0;
                return (
                  <tr key={u.resource.id} className="border-b border-neutral-100" data-testid={`usage-row-${u.resource.id}`}>
                    <td className="sticky left-0 bg-white z-10 px-3 py-2 border-r border-neutral-200 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-neutral-800">{u.resource.name}</span>
                        <span className="text-[10px] font-mono text-neutral-500">peak {u.peak_allocation}%</span>
                        {overFlag ? (
                          <span className="text-[10px] font-bold uppercase tracking-widest text-red-700 bg-red-50 border border-red-200 rounded px-1.5" data-testid={`usage-over-${u.resource.id}`}>
                            over × {u.over_days}
                          </span>
                        ) : null}
                      </div>
                    </td>
                    {allDates.map((d) => {
                      const cell = dayMap[d];
                      const alloc = cell?.allocation || 0;
                      const cap = u.resource.max_units_pct || 100;
                      const ratio = Math.min(1, alloc / Math.max(1, cap));
                      const bg = alloc === 0
                        ? "transparent"
                        : cell?.over
                          ? "#FCA5A5"
                          : `rgba(56,126,230,${0.15 + 0.7 * ratio})`;
                      const border = cell?.over ? "1px solid #DC2626" : "1px solid #F1F5F9";
                      return (
                        <td key={d} style={{ minWidth: 24, height: 24, background: bg, border }} title={alloc ? `${d}: ${alloc}%` : ""}>
                          {alloc > 0 && cell?.over ? (
                            <span className="block text-[8px] text-red-900 font-bold text-center">!</span>
                          ) : null}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
